/**
 * Customer-side "How to pay your host" card (Iter 63).
 *
 * Renders the chosen vendor payment method + actionable links so the customer
 * knows exactly how to settle the rental balance at pickup. Always-cash deposit
 * note included since the deposit is never routed through PayPal/Stripe.
 *
 * Reads `vendor_payment_method` + `vendor_payment_snapshot` snapshotted on the
 * booking at checkout time — so even if the vendor changes their PayPal email
 * later, past bookings keep their original instructions.
 */
import { Banknote, ExternalLink, ShieldCheck, Wallet } from "lucide-react";

function _label(m) {
  if (m === "cash") return "Cash at pickup";
  if (m === "paypal") return "PayPal";
  if (m === "stripe") return "Stripe (card)";
  return m || "—";
}

export default function BookingPayHostCard({ booking }) {
  if (!booking) return null;
  const method = booking.vendor_payment_method || "cash";
  const snap = booking.vendor_payment_snapshot || {};
  const balanceDue = Number(booking.rental_total || 0) - Number(booking.commission_amount || 0);
  const deposit = Number(booking.deposit || 0);

  return (
    <div className="rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="booking-pay-host-card">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="h-4 w-4 text-blue-600" />
        <h3 className="font-display text-base">How to pay your host</h3>
      </div>

      <div className="rounded-xl bg-neutral-50/60 border border-border px-3 py-2.5 mb-3">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-neutral-500">Rental balance</span>
          <span className="font-display text-lg font-semibold" data-testid="booking-pay-host-balance">€{balanceDue.toFixed(2)}</span>
        </div>
        <div className="text-[10px] text-neutral-400 mt-0.5">
          Total rental − platform commission (already paid online)
        </div>
      </div>

      {/* Method-specific block */}
      {method === "cash" && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 flex items-start gap-2" data-testid="booking-pay-host-cash">
          <Banknote className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900">
            <div className="font-semibold">Pay in cash at pickup</div>
            <div className="text-emerald-800">Bring €{balanceDue.toFixed(2)} (plus the deposit) to the handover.</div>
          </div>
        </div>
      )}
      {method === "paypal" && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5" data-testid="booking-pay-host-paypal">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="h-4 w-4 rounded-full bg-[#0070ba] flex items-center justify-center text-white text-[8px] font-bold">P</div>
            <span className="font-semibold text-sm text-blue-900">Pay via PayPal</span>
          </div>
          {snap.paypal_me_link && (
            <a href={snap.paypal_me_link} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline font-semibold"
               data-testid="booking-pay-host-paypal-link">
              Open {snap.paypal_me_link} <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {snap.paypal_email && (
            <div className="text-[11px] text-blue-800 mt-1">
              Or send to: <span className="font-mono">{snap.paypal_email}</span>
            </div>
          )}
          {!snap.paypal_me_link && !snap.paypal_email && (
            <div className="text-[11px] text-blue-800">Your host will share their PayPal details at pickup.</div>
          )}
          <div className="text-[10px] text-blue-700 mt-1.5">Send €{balanceDue.toFixed(2)} as "Friends & Family" if your host requests it.</div>
        </div>
      )}
      {method === "stripe" && (
        <div className="rounded-xl bg-violet-50 border border-violet-200 px-3 py-2.5" data-testid="booking-pay-host-stripe">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="h-4 w-4 rounded bg-[#635bff] flex items-center justify-center text-white text-[8px] font-bold">S</div>
            <span className="font-semibold text-sm text-violet-900">Pay via Stripe (card)</span>
          </div>
          {snap.stripe_payment_link ? (
            <a href={snap.stripe_payment_link} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-violet-700 hover:underline font-semibold"
               data-testid="booking-pay-host-stripe-link">
              Open payment link <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <div className="text-[11px] text-violet-800">Your host will share their Stripe payment link at pickup.</div>
          )}
          <div className="text-[10px] text-violet-700 mt-1.5">Pay €{balanceDue.toFixed(2)} via the link above before / at pickup.</div>
        </div>
      )}

      {/* Deposit reminder — always cash */}
      {deposit > 0 && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2" data-testid="booking-pay-host-deposit-note">
          <ShieldCheck className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[11px] text-amber-900">
            <strong>Deposit €{deposit.toFixed(2)} is always paid in cash at pickup</strong> — refunded at return if no damage.
          </div>
        </div>
      )}
    </div>
  );
}
